import React, { useState } from 'react';
import styled from 'styled-components';
import PageTemplate from '../../components/templates/PageTemplate';
import { MobileHeaderType } from '../../components/layout/ConfigurableMobileHeader';

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  padding: 0 24px;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  width: 100%;
`;

const StatCard = styled.div`
  background-color: #1E1B2E;
  border-radius: 8px;
  padding: 16px 20px;
  flex: 1;
  margin: 0 8px;
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  color: #FFFFFF;
  font-size: 24px;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const TimeframeContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const TimeframeButton = styled.button<{ $active: boolean }>`
  background-color: ${({ $active }) => $active ? '#9333EA' : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '500' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  
  &:hover {
    background-color: ${({ $active }) => $active ? '#A855F7' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const MetricContainer = styled.div`
  margin-bottom: 48px;
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const MetricTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #FFFFFF;
  text-transform: capitalize;
`;

const MetricChange = styled.div`
  font-size: 14px;
  color: #34C759;
`;

const MetricValues = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #FFFFFF;
`;

const MetricDate = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const ChartContainer = styled.div`
  height: 80px;
  width: 100%;
  position: relative;
  margin-top: 16px;
  display: flex;
  align-items: flex-end;
`;

const ChartLine = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
`;

const ChartMarker = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  background-color: #9333EA;
  border-radius: 50%;
  opacity: 0.5;
  left: 10%;
  top: -12px;
`;

const ReferButton = styled.button`
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #A855F7;
  }
`;

const PlusIcon = styled.span`
  margin-right: 8px;
  font-size: 18px;
  font-weight: 300;
`;

const Referrals: React.FC = () => {
  const [activeTimeframe, setActiveTimeframe] = useState('4w');
  
  const handleTimeframeChange = (timeframe: string) => {
    setActiveTimeframe(timeframe);
  };
  
  return (
    <PageTemplate
      mobileHeaderType={MobileHeaderType.SIMPLE}
      mobileHeaderTitle="Referrals"
      showBackButton={true}
      backPath="/wallet"
    >
      <PageContainer>
        <ContentWrapper>
          <StatsContainer>
            <StatCard>
              <StatLabel>Active Referrals</StatLabel>
              <StatValue>12</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Commissions</StatLabel>
              <StatValue>$20k</StatValue>
            </StatCard>
            <ReferButton>
              <PlusIcon>+</PlusIcon>
              Refer
            </ReferButton>
          </StatsContainer>
          
          <SectionTitle>Overview</SectionTitle>
          
          <TimeframeContainer>
            <TimeframeButton 
              $active={activeTimeframe === '1w'} 
              onClick={() => handleTimeframeChange('1w')}
            >
              1w
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === '4w'} 
              onClick={() => handleTimeframeChange('4w')}
            >
              4w
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === '1y'} 
              onClick={() => handleTimeframeChange('1y')}
            >
              1y
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'mtd'} 
              onClick={() => handleTimeframeChange('mtd')}
            >
              mtd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'qtd'} 
              onClick={() => handleTimeframeChange('qtd')}
            >
              qtd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'ytd'} 
              onClick={() => handleTimeframeChange('ytd')}
            >
              ytd
            </TimeframeButton>
            <TimeframeButton 
              $active={activeTimeframe === 'all'} 
              onClick={() => handleTimeframeChange('all')}
            >
              all
            </TimeframeButton>
          </TimeframeContainer>
          
          <MetricContainer>
            <MetricHeader>
              <MetricTitle>Commissions</MetricTitle>
              <MetricChange>+0.0%</MetricChange>
            </MetricHeader>
            <MetricValues>
              <div>
                <MetricValue>$0.00</MetricValue>
                <MetricDate>12 Jan - 12 Feb</MetricDate>
              </div>
              <div>
                <MetricValue>$0.00</MetricValue>
                <MetricDate>12 Feb - Today</MetricDate>
              </div>
            </MetricValues>
            <ChartContainer>
              <ChartLine>
                <ChartMarker />
              </ChartLine>
            </ChartContainer>
          </MetricContainer>
          
          <MetricContainer>
            <MetricHeader>
              <MetricTitle>Active Referrals</MetricTitle>
              <MetricChange>+0.0%</MetricChange>
            </MetricHeader>
            <MetricValues>
              <div>
                <MetricValue>124</MetricValue>
                <MetricDate>12 Jan - 12 Feb</MetricDate>
              </div>
              <div>
                <MetricValue>145</MetricValue>
                <MetricDate>12 Feb - Today</MetricDate>
              </div>
            </MetricValues>
            <ChartContainer>
              <ChartLine>
                <ChartMarker />
              </ChartLine>
            </ChartContainer>
          </MetricContainer>
        </ContentWrapper>
      </PageContainer>
    </PageTemplate>
  );
};

export default Referrals;
